// IM-JRN-007 — cliente da API de jornadas (IM-API-316..324). Nunca envia tenantId. A camada desligada responde 404 em
// todas as rotas: a interface trata 404 como "não existe" e não mostra nada sobre jornadas.
import type {
  CreateJourneyRequest, JourneyCatalogItemDto, JourneyDto, JourneyHistoryItemDto, JourneyLifecycle, JourneyLinkDto, JourneyLinkRequest, JourneyListItemDto,
  JourneyStepActionRequest, JourneyView, JourneyLinkEntityType, StepState,
} from '@intelimove/contracts';
import { api, ApiError } from '../api/client';

const J = '/api/v1/journeys';
export const journeysApi = {
  types: (signal?: AbortSignal) => api<{ items: JourneyCatalogItemDto[] }>(`${J}/types`, { signal }),
  list: (view: JourneyView, signal?: AbortSignal) => api<{ items: JourneyListItemDto[] }>(`${J}?view=${view}`, { signal }),
  create: (body: CreateJourneyRequest) => api<{ id: string; created: boolean; journey: JourneyDto }>(J, { method: 'POST', body }),
  get: (id: string, signal?: AbortSignal) => api<JourneyDto>(`${J}/${id}`, { signal }),
  transition: (id: string, body: { version: number; to: JourneyLifecycle; justification?: string }) => api<JourneyDto & { changed: boolean }>(`${J}/${id}/transition`, { method: 'POST', body }),
  step: (id: string, stepId: string, body: JourneyStepActionRequest) => api<JourneyDto & { changed: boolean }>(`${J}/${id}/steps/${stepId}`, { method: 'POST', body }),
  link: (id: string, body: JourneyLinkRequest) => api<{ created: boolean; link: JourneyLinkDto; journey: JourneyDto }>(`${J}/${id}/links`, { method: 'POST', body }),
  removeLink: (id: string, linkId: string, body: { version: number; justification: string }) => api<JourneyDto & { changed: boolean }>(`${J}/${id}/links/${linkId}/remove`, { method: 'POST', body }),
  history: (id: string, signal?: AbortSignal) => api<{ items: JourneyHistoryItemDto[] }>(`${J}/${id}/history?limit=100`, { signal }),
};

/** A camada está desligada (ou o recurso não existe): a interface se comporta como rota inexistente. */
export const isOff = (e: unknown) => e instanceof ApiError && e.status === 404;

export const STEP_TONE: Record<StepState, string> = {
  NAO_INICIADA: 'gray', PRONTA_PARA_COMECAR: 'action', EM_ANDAMENTO: 'action', AGUARDANDO_DADO: 'amber', AGUARDANDO_REVISAO: 'amber', AGUARDANDO_DECISAO: 'amber',
  BLOQUEADA: 'critical', CONCLUIDA: 'mint', CONCLUIDA_COM_LIMITACOES: 'mint', DESATUALIZADA: 'amber', DISPENSADA: 'gray',
};
export const LIFECYCLE_TONE: Record<string, string> = {
  NAO_INICIADA: 'gray', PRONTA_PARA_COMECAR: 'action', EM_ANDAMENTO: 'action', AGUARDANDO_DADO: 'amber', AGUARDANDO_REVISAO: 'amber', AGUARDANDO_DECISAO: 'amber', BLOQUEADA: 'critical',
  PAUSADA: 'gray', CONCLUIDA: 'mint', EM_ACOMPANHAMENTO: 'mint', CANCELADA: 'gray', ARQUIVADA: 'gray',
};
export const NEXT_KIND_LABEL: Record<string, string> = {
  DECIDIR: 'Decidir', REVISAR: 'Revisar', INFORMAR_DADO: 'Completar dado', DESBLOQUEAR: 'Resolver bloqueio', ACOMPANHAR_EXECUCAO: 'Acompanhar execução', EXECUTAR: 'Fazer agora',
  CONFIRMAR: 'Conferir e confirmar', ATUALIZAR: 'Atualizar', CONCLUIR_JORNADA: 'Concluir a jornada', ACOMPANHAR: 'Acompanhar efeitos',
};
/** Tela existente de cada tipo de item vinculado (deep link); `null` = sem tela própria. */
export function entityHref(type: JourneyLinkEntityType | string, id: string): string | null {
  switch (type) {
    case 'portfolio_analysis': return `/portfolio/${id}`;
    case 'battlecard': return `/battlecards/${id}`;
    case 'strategic_record': return `/decisoes/${id}`;
    case 'scenario_set': return `/cenarios/${id}`;
    case 'company_offering': return '/configuracoes/contexto/produtos';
    case 'company_context_version': return '/configuracoes/contexto';
    case 'price_series': return '/portfolio';
    case 'learning_report': return '/aprendizado';
    case 'company_competitor': return `/configuracoes/contexto/concorrentes?c=${id}`;
    case 'competitor_research_run': return '/configuracoes/contexto/concorrentes';
    case 'market_analysis': return `/mercado/${id}`;
    case 'market_run': return '/mercado';
    case 'source': return '/fontes';
    case 'signal': return '/sinais';
    default: return null;
  }
}
export const fmtDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
